import { ChevronDownIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, Img } from '@chakra-ui/react'
import { LandingContainer } from 'components/common/LandingContainer'
import ArrowRightOmens from 'components/svg/ArrowRightOmens'
import { Body1, H2, H3 } from 'components/typography'
import Link from 'next/link'
import { FC } from 'react'

import { HOME_CTA_HREF, HOME_CTA_LABEL } from '../constants'
import { HOME_HOW_IT_WORKS } from '../content'
import HomeTrustStrip from './HomeTrustStrip'

/**
 * "Como funciona a Medleve?" — layout of the current `MedleveHowItWorksSection` (trust strip, step
 * cards on the left, photo card with the CTA on the right) with the five PRD §14 steps as an ordered list.
 * REQUIRES MEDICAL REVIEW — copy in content.ts (`HOME_HOW_IT_WORKS`).
 */
const HomeHowItWorks: FC = () => (
  <Box as="section" aria-labelledby="home-how-it-works-title" bg="alabaster" color="phthalo">
    <LandingContainer bg="alabaster" py={{ base: 6, lg: 20 }} gap={{ base: 10, lg: 14 }} align="center" maxW="1400px">
      <HomeTrustStrip />
      <Flex
        w="full"
        direction={{ base: 'column', lg: 'row' }}
        justify="space-between"
        px={{ lg: '60px' }}
        gap={{ base: 6, lg: 36 }}
      >
        <Flex gap={4} direction="column" flex={1}>
          <H2 id="home-how-it-works-title" mb={4}>
            {HOME_HOW_IT_WORKS.title}
          </H2>
          <Flex as="ol" listStyleType="none" m={0} p={0} direction="column" gap={4}>
            {HOME_HOW_IT_WORKS.steps.map(({ title, description }, index) => (
              <Flex
                as="li"
                key={title}
                bg="white"
                borderRadius={{ base: '24px', lg: '32px' }}
                direction="column"
                p={6}
                gap={2}
              >
                <Flex justify="space-between" align="center">
                  <H3
                    fontWeight={600}
                    fontSize={{ base: '14px', lg: '15px' }}
                    lineHeight={{ base: '18px', lg: '20px' }}
                    letterSpacing="-0.01em"
                  >
                    {index + 1}. {title}
                  </H3>
                  <ChevronDownIcon fontSize="24px" color="ishafit.70" aria-hidden="true" />
                </Flex>
                <Body1>{description}</Body1>
              </Flex>
            ))}
          </Flex>
        </Flex>

        <Flex flex={1} direction="column">
          <Flex
            flex={1}
            position="relative"
            overflow="hidden"
            borderRadius={{ base: '24px', lg: '32px' }}
            align="center"
            justify="flex-end"
            direction="column"
            minH={{ base: '320px', lg: 'auto' }}
            py={12}
          >
            <Img
              src="/landings/caneta-emagrecedora-azul.webp"
              alt={HOME_HOW_IT_WORKS.imageAlt}
              htmlWidth={800}
              htmlHeight={846}
              loading="lazy"
              position="absolute"
              top={0}
              left={0}
              w="full"
              h="full"
              objectFit="cover"
            />
            <Box position="relative">
              <Link href={HOME_CTA_HREF}>
                <Button
                  h="auto"
                  w={{ base: '224px', lg: '304px' }}
                  px={{ base: '28px', lg: '32px' }}
                  py={{ base: '28px', lg: '40px' }}
                  borderRadius={{ base: '24px', lg: '32px' }}
                  color="alabaster"
                  bg="rgba(246, 244, 232, 0.0)"
                  backdropFilter="blur(24px)"
                  _hover={{ bg: 'rgba(246, 244, 232, 0.0)' }}
                >
                  <Flex w="full" justify="space-between" align="center">
                    <Body1
                      fontWeight={800}
                      fontSize={{ base: '1rem', lg: '1.5rem' }}
                      letterSpacing={{ base: '-0.01em', lg: '-0.03em' }}
                      lineHeight={{ base: '1.25rem', lg: '1.75rem' }}
                    >
                      {HOME_CTA_LABEL}
                    </Body1>
                    <ArrowRightOmens fill="alabaster" boxSize={{ base: '14px', lg: '18px' }} aria-hidden="true" />
                  </Flex>
                </Button>
              </Link>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </LandingContainer>
  </Box>
)

export default HomeHowItWorks
